import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Chip from "@material-ui/core/Chip";
import SearchIcon from "@material-ui/icons/Search";
import { format, parseISO } from "date-fns";
import Typography from "@material-ui/core/Typography";
import moment from "moment";
import ModernDatePicker from "../../components/ModernDatePicker";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import TableRowSkeleton from "../../components/TableRowSkeleton";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  filterContainer: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  avatar: {
    width: "30px",
    height: "30px",
    borderRadius: "50%"
  }
}));

const TagsReport = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [ticketTags, setTicketTags] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [count, setCount] = useState(0);

  const [initialDate, setInitialDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [finalDate, setFinalDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagsOptions, setTagsOptions] = useState([]);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const { data } = await api.get("/tags/", {
          params: { searchParam: "", pageNumber: 1, kanban: 0 },
        });
        setTagsOptions(data.tags);
      } catch (err) {
        toastError(err);
      }
    };
    fetchTags();
  }, []);

  const fetchReport = async (page = 1) => {
    setLoading(true);
    try {
      const { data } = await api.get("/tags-report", {
        params: {
          initialDate,
          finalDate,
          tags: JSON.stringify(selectedTags.map(t => t.id)),
          pageNumber: page,
          limit: rowsPerPage
        },
      });
      setTicketTags(data.ticketTags);
      setHasMore(data.hasMore);
      setCount(data.count);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReport(pageNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, rowsPerPage]);

  const handleSearch = () => {
    if (pageNumber === 1) {
      fetchReport(1);
    } else {
      setPageNumber(1);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPageNumber(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageNumber(1);
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Relatório de Tags ({count})</Title>
      </MainHeader>

      <Paper className={classes.filterContainer} variant="outlined">
        <ModernDatePicker
          label="Data Inicial"
          value={initialDate ? moment(initialDate).toDate() : null}
          onChange={(newValue) => setInitialDate(moment(newValue).format("YYYY-MM-DD"))}
        />
        <ModernDatePicker
          label="Data Final"
          value={finalDate ? moment(finalDate).toDate() : null}
          onChange={(newValue) => setFinalDate(moment(newValue).format("YYYY-MM-DD"))}
        />
        <Autocomplete
          multiple
          size="small"
          options={tagsOptions}
          getOptionLabel={(option) => option.name}
          value={selectedTags}
          onChange={(e, v) => setSelectedTags(v)}
          style={{ width: 300 }}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" label="Filtrar por Tags" placeholder="Tags" />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                style={{
                  backgroundColor: option.color || "#eee",
                  textShadow: "1px 1px #000",
                  color: "white",
                }}
                label={option.name}
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSearch}
          startIcon={<SearchIcon />}
          style={{
            backgroundColor: "#6517ab",
            color: "white",
          }}
        >
          Filtrar
        </Button>
      </Paper>

      <Paper
        className={classes.mainPaper}
        variant="outlined"
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Contato</TableCell>
              <TableCell align="center">Ticket ID</TableCell>
              <TableCell align="center">Tag</TableCell>
              <TableCell align="center">Data de Adição</TableCell>
              <TableCell align="center">Atendente</TableCell>
              <TableCell align="center">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ticketTags.map((tt) => (
              <TableRow key={tt.id}>
                <TableCell align="center">
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                    {tt.ticket?.contact?.profilePicUrl && (
                      <img src={tt.ticket.contact.profilePicUrl} alt="Contact" className={classes.avatar} />
                    )}
                    <Typography variant="body2">{tt.ticket?.contact?.name || "Desconhecido"}</Typography>
                  </div>
                </TableCell>
                <TableCell align="center">{tt.ticket?.id}</TableCell>
                <TableCell align="center">
                  {tt.tag ? (
                    <Chip
                      variant="outlined"
                      style={{
                        backgroundColor: tt.tag.color,
                        textShadow: "1px 1px #000",
                        color: "white",
                      }}
                      label={tt.tag.name}
                      size="small"
                    />
                  ) : "-"}
                </TableCell>
                <TableCell align="center">
                  {format(parseISO(tt.createdAt), "dd/MM/yyyy HH:mm")}
                </TableCell>
                <TableCell align="center">{tt.ticket?.user?.name || "-"}</TableCell>
                <TableCell align="center">{tt.ticket?.status || "-"}</TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={6} />}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[20, 50, 100]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={pageNumber - 1}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          labelRowsPerPage="Itens por página"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      </Paper>
    </MainContainer>
  );
};

export default TagsReport;
